/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { useState } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import isArray from 'lodash/isArray';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import { Dropdown, Modal } from 'carbon-addons-iot-react';

import * as utils from './schemaEditorUtils';

const propTypes = {
  /*
   * Formik path to the schema node that this input will show/modify the type of, e.g.:
   *
   * For the Formik data:
   *   {
   *     schema: {
   *        content: {
   *          properties: [
   *              {
   *                  name: "root",
   *                  type: "object",
   *                  properties: [
   *                      {
   *                          name: "leaf",
   *                          "type": "string",
   *                          default: "hello"
   *                          }
   *                      }
   *                  ]
   *              }
   *          ]
   *     }
   *   }
   *
   * This input will show/modify "leaf" if it's ID is set to "schema.content.properties[0].properties[0]"
   *
   * Both the "type" and "default" fields of the schema node will be affected (default is automatically cast to a type-appropriate value where possible)
   *
   */
  id: PropTypes.string.isRequired,
  readOnly: PropTypes.bool.isRequired,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,

  modal: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    contentText: PropTypes.string.isRequired,
    ariaLabel: PropTypes.string.isRequired,
    cancelActionText: PropTypes.string.isRequired,
    confirmActionText: PropTypes.string.isRequired,
  }).isRequired,
};

const defaultProps = {
  onChange: noop,
  placeholder: '',
};

const SchemaDatatypeInput = ({ id, readOnly, onChange, placeholder, modal }) => {
  const [field, , helpers] = useField(id);
  const [typeField, typeMeta] = useField(`${id}.type`);

  const [isShowingModal, setShowingModal] = useState(false);

  // used to hang on to data type(s) selected by user during the confirmation step shown
  // when switching away from array/object
  const [requestedNewDataTypes, setRequestedNewDataTypes] = useState(undefined);

  if (isNil(field.value) || field.value === '') {
    return null;
  }

  const commitDatatypeChange = newDataTypes => {
    // TODO: need to seek confirmation from the user if they are changing from object/array and
    // the prop has children that will be wiped as a result

    const newValue = {
      ...field.value,
      // type: isArray(newDataTypes) && newDataTypes.length===0 // clear type field is user removes
      //     ? undefined
      //     : newDataTypes,
      type: newDataTypes,
      default: utils.generateDefault(newDataTypes, field.value.default),
    };

    // If newDatatypes includes array, ensure the property has 'items'
    if (utils.isOrIncludesArray(newDataTypes)) {
      newValue.items = {
        type: undefined,
        required: false,
        allowNull: false,
      };
    } else {
      // otherwise ensure items is removed
      delete newValue.items;
    }

    // If newDatatypes includes object, ensure the property has 'properties'
    if (utils.isOrIncludesObject(newDataTypes)) {
      newValue.properties = [];
    } else {
      // otherwise ensure 'properties' is removed
      delete newValue.properties;
    }

    // Recompute the default value field in case we need to coerce it into a new type
    helpers.setValue(newValue);

    onChange(newValue);
  };

  const onDatatypeChange = newDataTypes => {
    // if we are switching type from being object AND it has >0 properties defined
    // OR if it's an array AND its item type has been set
    // prompt the user for confirmation that it's ok for us to remove properties/items
    if (
      (utils.isOrIncludesObject(typeField.value) && !isEmpty(get(field, 'value.properties'))) ||
      (utils.isOrIncludesArray(typeField.value) && !isNil(get(field, 'value.items.type')))
    ) {
      setRequestedNewDataTypes(newDataTypes);
      setShowingModal(true);
    } else {
      commitDatatypeChange(newDataTypes);
    }
  };

  return (
    <>
      {isShowingModal ? (
        <Modal
          danger
          modalHeading={modal.heading}
          modalAriaLabel={modal.ariaLabel}
          iconDescription={modal.cancelActionText}
          isLarge={false}
          onRequestClose={() => {
            setRequestedNewDataTypes(undefined);
            setShowingModal(false);
          }}
          onRequestSubmit={() => {
            commitDatatypeChange(requestedNewDataTypes);
            setRequestedNewDataTypes(undefined);
            setShowingModal(false);
          }}
          onSecondarySubmit={() => {
            setRequestedNewDataTypes(undefined);
            setShowingModal(false);
          }}
          open
          primaryButtonText={modal.confirmActionText}
          secondaryButtonText={modal.cancelActionText}>
          {modal.contentText}
        </Modal>
      ) : null}

      {
        // Only show multiple selection input if the user has manually
        // set the type to be polymorphic via the source view (or the API)
        // NOTE: this does not include null ( since we treat null as a special type
        // in the UI that must be specified via the "allow null" check in "validation constraints" modal)
        isArray(typeField.value) /*
                        Disabled polymorphic type support in table view for now. Will add back (and fix all the bugs) if users request it
                        <MultiSelect
                            name={id}
                            // due to limited horizontal room in table, show elipsis when multiple data types are selected
                            label={() => isNil(typeField.value) ? '' : isArray(typeField.value) ? '...' : typeField.value}
                            items={Object.values(utils.JSON_SCHEMA_DATATYPES_EXCLUDING_NULL)}
                            itemToString={i => i}
                            onChange={(e) => onDatatypeChange(e.selectedItems)}
                            onBlur={typeField.onBlur}
                            initialSelectedItems={utils.ensureArray(typeField.value)}
                            invalid={typeMeta.error}
                            invalidText={typeMeta.error}
                            disabled={readOnly}
                        />
                        */ ? // for now, just omit the type field if the user has defined a polymorphic type in source view (or via the apis)
        null : (
          <Dropdown
            id={id}
            name={id}
            items={Object.values(utils.JSON_SCHEMA_DATATYPES_EXCLUDING_NULL)}
            onChange={e => onDatatypeChange(e.selectedItem)}
            invalid={typeMeta.error && typeMeta.touched}
            invalidText={typeMeta.error}
            disabled={readOnly}
            label={placeholder}
            initialSelectedItem={typeField.value}
            selectedItem={typeField.value}
          />
        )
      }
    </>
  );
};

SchemaDatatypeInput.propTypes = propTypes;
SchemaDatatypeInput.defaultProps = defaultProps;

export default SchemaDatatypeInput;
